import update from "immutability-helper";

import { combineReducers, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Case, Order } from "@types-runway/case";
import { Condition, Medication } from "@types-runway/condition";
import { RootEntity } from "@types-runway/state";

const orderObject: Order = {
  id: 0,
  order_id: "",
  status: "",
  UserId: "",
  carrier: "",
  caseId: "",
  createdAt: "",
  updatedAt: "",
  shipping_method: null,
  tracking_number: "",
  departureDate: "",
};
export const caseObject: RootEntity<Case> = {
  isPending: false,
  success: { type: null },
  data: {
    caseId: null,
    conditions: [],
    invoiceId: null,
    case_status: null,
    case_questions: [],
    shipping_method: null,
    CurexaOrder: orderObject,
    updatedAt: Date.now().toString(),
    paid: false,
    totalQuestions: 0,
    UserId: null,
    trip: {
      from: "",
      to: "",
      days: 0,
    },
  },
  error: {},
};

const newCaseState: RootEntity<Case> = {
  isPending: false,
  success: { type: null },
  data: caseObject.data,
  error: {},
};

export const newCaseSlice = createSlice({
  name: "newCase",
  initialState: newCaseState,
  reducers: {
    addConditions: (state, action) => {
      const uniqueConditions = [...state.data.conditions];

      // Add new conditions while avoiding duplicates
      action.payload.forEach((newCondition: Condition) => {
        const isDuplicate = uniqueConditions.some(
          (existingCondition) => existingCondition.product.name === newCondition.product.name
        );

        if (!isDuplicate) {
          uniqueConditions.push(newCondition);
        }
      });

      // Update state with unique conditions
      return update(state, {
        isPending: {
          $set: false,
        },
        data: {
          conditions: {
            $set: uniqueConditions,
          },
        },
      });
    },
    removeConditions: (state, action) => {
      const indexOfCondition = state.data.conditions.findIndex(
        (condition) => condition.product.name === action.payload
      );
      return update(state, {
        data: {
          conditions: {
            $splice: [[indexOfCondition, 1]],
          },
        },
      });
    },
    setConditions: (state, action) => {
      let uniqueConditions: Condition[] = [];
      // Set conditions while avoiding duplicates
      action.payload.conditions.forEach((condition: Condition) => {
        const isDuplicate = uniqueConditions.some(
          (existingCondition) => existingCondition.product.name === condition.product.name
        );

        if (!isDuplicate) {
          uniqueConditions.push(condition);
        }
      });

      // Update state with unique conditions
      return update(state, {
        isPending: {
          $set: false,
        },
        data: {
          conditions: {
            $set: uniqueConditions,
          },
        },
      });
    },
    setConditionUnits: (state, action) => {
      const indexOfCondition = state.data.conditions.findIndex(
        (condition) => condition.product.id === action.payload.productId
      );
      return update(state, {
        data: {
          conditions: {
            [indexOfCondition]: {
              product: {
                units: {
                  $set: action.payload.units,
                },
              },
            },
          },
        },
      });
    },
    updateShippingMethod: (state, action) => {
      return {
        ...state,
        data: { ...state.data, shipping_method: action.payload },
      };
    },
    updateCaseQuestions: (state, action) => {
      return {
        ...state,
        data: { ...state.data, case_questions: action.payload },
      };
    },
    setUser: (state, action) => {
      return {
        ...state,
        data: { ...state.data, UserId: action.payload },
      };
    },
    setShippingAddress: (state, action) => {
      return {
        ...state,
        data: { ...state.data, shippingAddress: action.payload },
      };
    },
    newCasePending: (state) => {
      return Object.assign({}, state, { isPending: true });
    },
    newCaseSuccess: (state, action) => {
      return Object.assign({}, state, {
        data: action.payload,
        isPending: false,
      });
    },
    newCaseFailure: (state, action) => {
      return Object.assign({}, state, { error: action.payload, isPending: false });
    },
    setUpsellState: (state, action) => {
      return {
        ...state,
        isPending: false,
        data: { ...state.data, upsell: action.payload },
      };
    },
    removeUpsellConditions: (state, action) => {
      const indexOfCondition =
        state.data.upsell?.conditions.findIndex(
          (condition: Condition) => condition.product.name === action.payload
        ) ?? -1;

      if (indexOfCondition >= 0) {
        return update(state, {
          data: {
            upsell: {
              conditions: {
                $splice: [[indexOfCondition, 1]],
              },
            },
          },
        });
      }
    },
    updateConditionMedication: (
      state,
      action: PayloadAction<{ conditionName: string; medication: Medication }>
    ) => {
      const condition = state.data.conditions.find(
        (c) => c.product.name === action.payload.conditionName
      );
      if (condition) {
        condition.suggestedMedication = {
          ...(condition.suggestedMedication ?? {}),
          ...action.payload.medication,
        };
      }
    },
    clearNewCase: (state) => {
      return Object.assign({}, state, newCaseState);
    },
  },
});

const casesArray: RootEntity<Case>[] = [];

const casesState: RootEntity<RootEntity<Case>[]> = {
  isPending: false,
  success: { type: null },
  data: casesArray,
  error: {},
};

export const casesSlice = createSlice({
  name: "cases",
  initialState: casesState,
  reducers: {
    getCasesPending: (state) => {
      return Object.assign({}, state, { isPending: true });
    },
    getCasesSuccess: (state, action) => {
      // We merge open cases with closed ones.
      const incomingCases = new Set<number>(action.payload.map((case_: Case) => case_.id));

      const newState = state.data.filter((case_) => {
        const incomingCase = incomingCases.has(case_.data.id || 0);

        if (!incomingCase) {
          return case_;
        }
      });

      const cases = action.payload.map((case_: any) => {
        case_ = {
          isPending: false,
          data: {
            ...case_,
            conditions: case_.conditions || [],
          },
          error: {},
        };
        return case_;
      });

      return Object.assign({}, state, {
        isPending: false,
        success: { type: "read" },
        data: newState.concat(cases),
      });
    },
    getCasesFailure: (state, action) => {
      return Object.assign({}, state, { isPending: false, error: action.payload });
    },
    caseMutationPending: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      // Deep nesting update.
      return update(state, {
        // Cases layer
        data: {
          // ID of the case.
          // Single case layer
          [indexOfCase]: {
            isPending: {
              $set: true,
            },
          },
        },
      });
    },
    getInvoiceSuccess: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      return update(state, {
        // Cases layer
        data: {
          // Single case layer.
          [indexOfCase]: {
            data: {
              CurexaOrder: {
                invoice: {
                  $set: action.payload.data,
                },
              },
            },
          },
        },
      });
    },
    getInvoiceUnavailable: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      return update(state, {
        data: {
          [indexOfCase]: {
            data: {
              CurexaOrder: {
                isInvoiceUnavailable: {
                  $set: true,
                },
              },
            },
          },
        },
      });
    },
    updateOrderSuccess: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      return update(state, {
        data: {
          [indexOfCase]: {
            isPending: {
              $set: false,
            },
            data: {
              CurexaOrder: {
                $set: action.payload.data,
              },
            },
          },
        },
      });
    },
    updateCaseSuccess: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      return update(state, {
        data: {
          [indexOfCase]: {
            isPending: {
              $set: false,
            },
            data: {
              $set: action.payload.data,
            },
          },
        },
      });
    },
    addCaseToFeed: (state, action) => {
      return update(state, {
        data: {
          $push: action.payload,
        },
      });
    },
    caseMutationFailure: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload?.caseId);
      return update(state, {
        data: {
          // ID of the case.
          [indexOfCase]: {
            isPending: {
              $set: false,
            },
            error: {
              $set: action.payload.error,
            },
          },
        },
      });
    },
    paymentAttemptRecorded: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      if (indexOfCase < 0) {
        return state;
      }
      return update(state, {
        data: {
          [indexOfCase]: {
            isPending: {
              $set: false,
            },
            error: {
              $set: { paymentOutcome: action.payload.outcome },
            },
            // The attempt may have created the invoice, so the summary asks for it again.
            data: {
              CurexaOrder: {
                $unset: ["isInvoiceUnavailable"],
              },
            },
          },
        },
      });
    },
    cardPaymentReviewOpened: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      if (indexOfCase < 0) {
        return state;
      }
      return update(state, {
        data: {
          [indexOfCase]: {
            isPending: { $set: false },
            data: { CurexaOrder: { isAwaitingCardConfirmation: { $set: true } } },
          },
        },
      });
    },
    cardPaymentReviewClosed: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      if (indexOfCase < 0) {
        return state;
      }
      return update(state, {
        data: {
          [indexOfCase]: {
            data: { CurexaOrder: { $unset: ["isAwaitingCardConfirmation"] } },
          },
        },
      });
    },
    dismissPaymentError: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload.caseId);
      if (indexOfCase < 0) {
        return state;
      }
      return update(state, {
        data: {
          [indexOfCase]: {
            error: {
              $set: {},
            },
          },
        },
      });
    },
    setCasePendingPayment: (state, action) => {
      const indexOfCase = state.data.findIndex((case_) => case_.data.id === action.payload?.caseId);
      return update(state, {
        data: {
          // ID of the case.
          [indexOfCase]: {
            isPending: {
              $set: false,
            },
            data: {
              invoiceId: {
                $set: null,
              },
            },
          },
        },
      });
    },
    clearCases: () => {
      return casesState;
    },
  },
});

export const {
  newCasePending,
  addConditions,
  removeConditions,
  setConditions,
  setConditionUnits,
  newCaseSuccess,
  updateShippingMethod,
  updateCaseQuestions,
  setUser,
  setShippingAddress,
  newCaseFailure,
  setUpsellState,
  removeUpsellConditions,
  updateConditionMedication,
  clearNewCase,
} = newCaseSlice.actions;

export const {
  getCasesPending,
  getCasesSuccess,
  getCasesFailure,
  caseMutationPending,
  getInvoiceSuccess,
  getInvoiceUnavailable,
  updateOrderSuccess,
  updateCaseSuccess,
  addCaseToFeed,
  caseMutationFailure,
  paymentAttemptRecorded,
  cardPaymentReviewOpened,
  cardPaymentReviewClosed,
  dismissPaymentError,
  setCasePendingPayment,
  clearCases,
} = casesSlice.actions;

export default combineReducers({
  newCase: newCaseSlice.reducer,
  cases: casesSlice.reducer,
});
